import React, { useState, useEffect } from "react";
import shopify from '../../../../../js/shopify';
import {Paragraph} from '../../../elements/Paragraph/Paragraph';
import { mobileBreakpoint } from "../../../../../entrypoints/theme";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import "./CartSummary.scss";

export const CartSummary = ({ settings }) => {
  const { section, routes, blocks } = settings;
  const [cart, setCart] = useState(settings.cart);
  const [products, setProducts] = useState([]);
  const isMobile = useMobileBreakpoint(mobileBreakpoint);
  const membershipBlock = blocks.length ? blocks.find((block) => block.type === "membership_perks") : null;
  const isLoggedIn = settings.globals.is_customer_logged_in; // Check if customer object exists

  useEffect(() => {
    const productIdsList = [...new Set(cart.items.map((product) => product.product_id))];
    async function fetchProducts(productIds) {
      const products = await shopify.getShopifyProductList(productIds);
      setProducts(products);
    }

    fetchProducts(productIdsList);
    document.addEventListener("liquid-ajax-cart:request-end", function (event: any) {
      const { requestState, cart, previousCart, sections } = event.detail;
      setCart(cart);
    });

    return () => {
      window.removeEventListener("liquid-ajax-cart:request-end", () => {});
    };
  }, []);

  const formatMoney = (cents) => {
    const dollars = cents / 100;
    return dollars.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };


  let sectionHeadingClass = "h4";
  let pricesClass = "h4";
  let shippingInfoClass = "b2";

    if (isMobile) {
      sectionHeadingClass = "h5";
      pricesClass = "h4";
      shippingInfoClass = "b2";
    }

  const paymentBlock = blocks.length ? blocks.find((block) => block.type === "payment_icons") : null;
  const cart_subtotal = cart.total_price;

  const totalSavings = cart.items.reduce((acc, item) => {
    const product = products.find(p => p.id === item.product_id);
    if (item.selling_plan_allocation) {
      // Subscription savings
      const originalPrice = item.selling_plan_allocation.compare_at_price;
      const discountedPrice = item.selling_plan_allocation.price;
      const itemSavings = ((originalPrice - discountedPrice) / 100) * item.quantity;
      return acc + itemSavings;
    } else if (item.line_level_discount_allocations?.length) {
      // Discount savings

      let itemDiscount = 0;
      item.line_level_discount_allocations.forEach(discount => {
        itemDiscount += discount.amount;
      });
      const itemSavings = itemDiscount / 100;
      return acc + itemSavings;
    } else if (product?.compare_at_price) {
      // Compare at price savings
      const originalPrice = product.compare_at_price;
      const discountedPrice = item.price;
      const itemSavings = ((originalPrice - discountedPrice) / 100) * item.quantity;
      return acc + itemSavings;
    }
    return acc;
  }, 0);

  const cartSubtotal = Number((cart_subtotal / 100).toFixed(2));  

  const totalBeforeSavings = totalSavings + cartSubtotal;
  const hasSavings = totalSavings > 0;
  const currencySymbol = window.Alpine?.store('currency').getCurrencySymbol();
  const moneyBackTextStyle = section?.money_back_text_style || 'item-2';
  const freeShippingTextStyle = section?.free_shipping_text_style || 'item-2';

  return (
      <div className="cart__rhs cart__summary">
      {section.show_usp && isMobile && (
        <div className="minicart__usp rounded-lg my-1 mb-4">
          <div className="flex items-center justify-between gap-2 lg:gap-4">
            {section?.money_back_text && (
                <div className="flex items-center">
                {section.icomoon_icon_1 ? (
                  <span className={`${section.icomoon_icon_1} text-orange-600 mr-2 text-[20px] lg:text-[24px]`}></span>
                ) : section.icon_1 ? (
                  <img 
                    src={section.icon_1} 
                    alt="Money back guarantee icon"
                    className="w-6 h-6 mr-2"
                  />
                ) : (
                  <span className="icon-like text-orange-600 mr-2 text-[20] lg:text-[24px]"></span>
                )}
                <Paragraph 
                  classes={moneyBackTextStyle} 
                  text={section.money_back_text}
                />
              </div>
            )}
            
            {section?.free_shipping_text && (
              <div className="flex items-center">
                {section.icomoon_icon_2 ? (
                  <span className={`${section.icomoon_icon_2} text-orange-600 mr-2 text-[20px] lg:text-[24px]`}></span>
                ) : section.icon_2 ? (
                  <img 
                    src={section.icon_2} 
                    alt="free shipping icon"
                    className="w-6 h-6 mr-2"
                  />
                ) : (
                  <span className="icon-car text-orange-600 mr-2 text-[20px] lg:text-[24px]"></span>
                )}
                <Paragraph 
                  classes={freeShippingTextStyle} 
                  text={section.free_shipping_text}
                />
              </div>
            )}
          </div>
        </div>
        )}
        {membershipBlock && !isLoggedIn && !isMobile && (
        <div 
          className="membership-perks rounded-xl p-2" 
          style={{ backgroundColor: membershipBlock.settings.background_color }}
        >
          <div className="flex items-start flex-col gap-2">
            <div className="flex items-center gap-2">
              <div>
              {membershipBlock.settings.icomoon_icon ? (
              <span className={`${membershipBlock.settings.icomoon_icon} text-orange-600 text-[24px]`}></span>
            ) : membershipBlock.settings.icon ? (
              <img 
                src={membershipBlock.settings.icon} 
                alt="Membership icon"
                className="w-6 h-6"
              />
            ) : null}

              </div>
              {membershipBlock.settings.title && (
                <h3 className={membershipBlock.settings.title_style}>
                  {membershipBlock.settings.title}
                </h3>
              )}
              </div>
            
            
            <div className="flex gap-3 items-center">
              {membershipBlock.settings.description && (
                <div className={membershipBlock.settings.description_style}>
                  {membershipBlock.settings.description}
                </div>
              )}

              <div className="flex gap-2">
                <a href="/account/register" className="b4--bold underline">
                  Sign up
                </a>
                <div>/</div>
                <a href="/account/login" className="b4--bold underline">
                  Login
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
        <div className="cart__summary-top"></div>
        <div className="flex flex-col gap-4">
        <form
            id="cart-summary-form"
            action="/checkout"
            method="post"
            className={`order-[${section.summary_order}]`}
        >
          <h3 className={`h5 text-left hidden lg:block py-[32px] ${isLoggedIn && section.summary_order === '1' ? 'pt-0' : ''}`}>
            {section.order_summary_title}
          </h3>

          <div
            className={` minicart__totals flex-col w-full py-4 px-4 rounded-xl `}
            style={{ backgroundColor: section.summary_background_color }}
          >
            {section.show_subtotal && (
              <div className="minicart__totals-row w-full flex justify-between items-center pb-3">
                <Paragraph classes={'b--cart'} text={'Subtotal'} />
                <Paragraph 
                  classes={'b--cart'}
                  text={`${currencySymbol}${totalBeforeSavings.toFixed(2)}`} 
                />
              </div>
            )}

            {section.show_savings && totalSavings > 0 && (
                 <div className="minicart__totals-row  w-full flex justify-between items-center  pb-3">
                 <Paragraph classes={'b--cart'} text={'Saving'} />
                 <div className="">
                   <Paragraph 
                     classes={'b--cart'} 
                     text={`-${currencySymbol}${totalSavings.toFixed(2)}`} 
                   />
                 </div>
               </div>
            )}
             {section.show_shipping_info && section.shipping_info_location === 'in_totals' && (
              <div className="minicart__totals-row w-full flex justify-between items-center pb-3">
                <Paragraph classes={'b6 text-left'} text={section.shipping_note_text} />
              </div>
            )}
            

            <div className="minicart__totals-row flex  w-full justify-between items-center">
            <Paragraph classes={'b2--cart'} text={'Total'} />
            <Paragraph 
              classes={'b2--bold--cart'} 
              text={`${formatMoney(cart.items_subtotal_price.toFixed(hasSavings ? 2 : 0))}`} 
            />
          </div>
          <button className="button button-primary minicart-actions__checkout mt-4" type="submit" name="checkout" form="cart-summary-form">
            Checkout
          </button>
            
          </div>
          
          {section.show_cart_note && (
              <div className="cart_notes">
                <label htmlFor="cart_note">Cart Notes</label>
                <input
                    className="cart_notes-input"
                    type="text"
                    name="cart_note"
                    id="cart_note"
                    placeholder="Enter notes for cart"
                />
              </div>
          )}

          {section.show_shipping_info && section.shipping_info_location === 'below_button' && (
               <div className="minicart__totals-row w-full flex justify-between items-center py-3">
               <Paragraph classes={'b4'} text={section.shipping_note_text} />
             </div>
            )}
          {paymentBlock?.settings.payment_icons && (
              <div className="payment-icons">
                <img
                    src={paymentBlock.settings.payment_icons}
                    alt="Payment icons"
                    className="mx-auto"
                />
              </div>
          )}
        </form>
          <div  className={`help-section cart-info-1 order-[${section.information_1_order}]`}>
            <h4 className={`help-section-title ${sectionHeadingClass}`}>
              {section.cart_information_1_title}
            </h4>
            <Paragraph
              type='b2'
              text={`${section.cart_information_1_description}`}
            />
          </div>
          <div className={`shipping-section cart-info-2 order-[${section.information_2_order}]`}>
          <h4 className={`shipping-section-title ${sectionHeadingClass}`}>
              {section.cart_information_2_title}
            </h4>
            <Paragraph
              type='b2'
              text={`${section.cart_information_2_description}`}
            />
          </div>
        </div>
        
      </div>
  );
};
